import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  ListChecks,
  Package,
  ClipboardList,
  GripVertical,
  User,
  Crown,
  Baby,
  HeartPulse,
  Users,
  X,
  Save,
  Target,
  Award,
  CheckSquare,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { getSupabaseAdminClient, ChecklistTemplate } from '@parentpath/supabase-client';
import toast from 'react-hot-toast';
import { LocaleSelector } from '@/components/LocaleSelector';
import { useAdminLocale } from '@/hooks/useAdminLocale';

const supabase = getSupabaseAdminClient();

type StatusFilter = 'all' | 'active' | 'inactive';

interface ChecklistWithItems extends ChecklistTemplate {
  items_count: number;
}

interface TemplateItem {
  id: string;
  template_id: string | null;
  title: string;
  description: string | null;
  category: string | null;
  quantity: string | null;
  priority: 'essential' | 'recommended' | 'optional' | null;
  estimated_cost_eur: number | null;
  order_index: number;
}

interface ItemFormData {
  id?: string;
  title: string;
  description: string;
  quantity: string;
  priority: 'essential' | 'recommended' | 'optional' | null;
  estimated_cost_eur: number | null;
}

interface TemplateFormData {
  title: string;
  description: string;
  category: string;
  order_index: number;
  is_active: boolean;
  visible_to_free: boolean;
  target_mode: 'pregnancy' | 'baby' | 'both';
  items: ItemFormData[];
}

const CHART_COLORS = ['#14b8a6', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981', '#6366f1'];

const CATEGORY_LABELS: Record<string, string> = {
  'hospital_bag': 'Kliniktasche',
  'baby_equipment': 'Baby-Ausstattung',
  'nursery': 'Kinderzimmer',
  'documents': 'Dokumente',
  'postpartum': 'Wochenbett',
  'general': 'Allgemein',
};

// --- Template Form Modal (with items management) ---
function TemplateFormModal({
  template,
  onClose,
  onSave,
  isSaving,
}: {
  template?: ChecklistWithItems;
  onClose: () => void;
  onSave: (data: TemplateFormData) => void;
  isSaving: boolean;
}) {
  const isEdit = !!template;
  const [formData, setFormData] = useState({
    title: template?.title || '',
    description: template?.description || '',
    category: template?.category || 'general',
    order_index: template?.order_index || 0,
    is_active: template?.is_active ?? true,
    visible_to_free: template?.visible_to_free ?? false,
    target_mode: (template?.target_mode || 'both') as 'pregnancy' | 'baby' | 'both',
  });
  const [items, setItems] = useState<ItemFormData[]>([]);
  const [expandedItem, setExpandedItem] = useState<number | null>(null);
  const [itemsLoaded, setItemsLoaded] = useState(!isEdit);

  // Load existing items when editing
  useEffect(() => {
    if (!template) return;
    (async () => {
      const { data, error } = await supabase
        .from('checklist_template_items')
        .select('*')
        .eq('template_id', template.id)
        .order('order_index', { ascending: true });

      if (!error && data) {
        setItems(
          (data as TemplateItem[]).map((item) => ({
            id: item.id,
            title: item.title,
            description: item.description || '',
            quantity: item.quantity || '',
            priority: item.priority,
            estimated_cost_eur: item.estimated_cost_eur,
          }))
        );
      }
      setItemsLoaded(true);
    })();
  }, [template]);

  const addItem = () => {
    setItems([...items, {
      title: '',
      description: '',
      quantity: '',
      priority: null,
      estimated_cost_eur: null,
    }]);
    setExpandedItem(items.length);
  };

  const updateItem = (index: number, updates: Partial<ItemFormData>) => {
    setItems(items.map((item, i) => i === index ? { ...item, ...updates } : item));
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
    if (expandedItem === index) setExpandedItem(null);
    else if (expandedItem !== null && expandedItem > index) setExpandedItem(expandedItem - 1);
  };

  const moveItem = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    const newItems = [...items];
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    setItems(newItems);
    if (expandedItem === index) setExpandedItem(newIndex);
    else if (expandedItem === newIndex) setExpandedItem(index);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Titel ist erforderlich');
      return;
    }
    const emptyItems = items.filter(i => !i.title.trim());
    if (emptyItems.length > 0) {
      toast.error('Alle Elemente brauchen einen Titel');
      return;
    }
    onSave({ ...formData, items });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-xl font-bold text-slate-900">
            {isEdit ? 'Vorlage bearbeiten' : 'Neue Vorlage erstellen'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          {/* Template fields */}
          <div className="p-6 space-y-4 border-b border-slate-200">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Titel *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                placeholder="z.B. Kliniktasche"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Beschreibung</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                rows={2}
                placeholder="Kurze Beschreibung der Checkliste..."
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Kategorie</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                >
                  {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Zielgruppe</label>
                <select
                  value={formData.target_mode}
                  onChange={(e) => setFormData({ ...formData, target_mode: e.target.value as 'pregnancy' | 'baby' | 'both' })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                >
                  <option value="pregnancy">Schwangerschaft</option>
                  <option value="baby">Baby</option>
                  <option value="both">Beide</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Reihenfolge</label>
                <input
                  type="number"
                  value={formData.order_index}
                  onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                />
              </div>
              <div className="flex flex-col gap-2 pt-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300 text-teal-500 focus:ring-teal-500"
                  />
                  <span className="text-sm text-slate-700">Aktiv</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.visible_to_free}
                    onChange={(e) => setFormData({ ...formData, visible_to_free: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300 text-teal-500 focus:ring-teal-500"
                  />
                  <span className="text-sm text-slate-700">Für Free</span>
                </label>
              </div>
            </div>
          </div>

          {/* Items section */}
          <div className="p-6 space-y-3">
            <h3 className="text-sm font-semibold text-slate-700">Elemente ({items.length})</h3>

            {!itemsLoaded ? (
              <div className="text-center py-4 text-slate-500 text-sm">Elemente werden geladen...</div>
            ) : items.length === 0 ? (
              <div className="text-center py-6 text-slate-400 text-sm border-2 border-dashed border-slate-200 rounded-lg">
                Noch keine Elemente. Klicke auf "Element hinzufügen" um loszulegen.
              </div>
            ) : (
              <div className="space-y-2">
                {items.map((item, index) => {
                  const isExpanded = expandedItem === index;
                  return (
                    <div key={index} className="border border-slate-200 rounded-lg bg-slate-50">
                      {/* Collapsed row */}
                      <div className="flex items-center gap-2 p-3">
                        <div className="flex flex-col gap-0.5">
                          <button
                            type="button"
                            onClick={() => moveItem(index, 'up')}
                            disabled={index === 0}
                            className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ChevronUp className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveItem(index, 'down')}
                            disabled={index === items.length - 1}
                            className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ChevronDown className="w-3 h-3" />
                          </button>
                        </div>
                        <span className="text-xs text-slate-400 w-5 text-right">{index + 1}.</span>
                        <input
                          type="text"
                          value={item.title}
                          onChange={(e) => updateItem(index, { title: e.target.value })}
                          className="flex-1 px-2 py-1 text-sm border border-slate-300 rounded focus:ring-1 focus:ring-teal-500 focus:border-teal-500 outline-none bg-white"
                          placeholder="Element-Titel *"
                        />
                        {item.priority && (
                          <span className={`px-1.5 py-0.5 text-xs rounded flex-shrink-0 ${
                            item.priority === 'essential' ? 'bg-red-100 text-red-700' :
                            item.priority === 'recommended' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {item.priority === 'essential' ? 'Ess.' : item.priority === 'recommended' ? 'Empf.' : 'Opt.'}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => setExpandedItem(isExpanded ? null : index)}
                          className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded"
                          title="Details"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                          title="Entfernen"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {/* Expanded details */}
                      {isExpanded && (
                        <div className="px-3 pb-3 pt-0 border-t border-slate-200 mt-0">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3">
                            <div className="md:col-span-2">
                              <label className="block text-xs text-slate-500 mb-1">Beschreibung</label>
                              <textarea
                                value={item.description}
                                onChange={(e) => updateItem(index, { description: e.target.value })}
                                className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded focus:ring-1 focus:ring-teal-500 outline-none bg-white"
                                rows={2}
                                placeholder="Optionale Beschreibung..."
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">Priorität</label>
                              <select
                                value={item.priority || ''}
                                onChange={(e) => updateItem(index, { priority: (e.target.value || null) as ItemFormData['priority'] })}
                                className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded focus:ring-1 focus:ring-teal-500 outline-none bg-white"
                              >
                                <option value="">Keine</option>
                                <option value="essential">Essentiell</option>
                                <option value="recommended">Empfohlen</option>
                                <option value="optional">Optional</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">Menge</label>
                              <input
                                type="text"
                                value={item.quantity}
                                onChange={(e) => updateItem(index, { quantity: e.target.value })}
                                className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded focus:ring-1 focus:ring-teal-500 outline-none bg-white"
                                placeholder="z.B. 3-5 Stück"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">Geschätzte Kosten (€)</label>
                              <input
                                type="number"
                                value={item.estimated_cost_eur ?? ''}
                                onChange={(e) => updateItem(index, { estimated_cost_eur: e.target.value ? parseFloat(e.target.value) : null })}
                                className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded focus:ring-1 focus:ring-teal-500 outline-none bg-white"
                                placeholder="0"
                                min={0}
                                step={0.01}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <button
              type="button"
              onClick={addItem}
              className="w-full px-3 py-2 text-sm border-2 border-dashed border-slate-300 text-slate-500 rounded-lg hover:bg-slate-50 flex items-center justify-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" />
              Element hinzufügen
            </button>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t border-slate-200 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {isSaving ? 'Speichert...' : isEdit ? 'Speichern' : 'Erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Template View Modal ---
function TemplateViewModal({
  template,
  onClose,
}: {
  template: ChecklistWithItems;
  onClose: () => void;
}) {
  const { data: items, isLoading } = useQuery({
    queryKey: ['checklist-template-items', template.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('checklist_template_items')
        .select('*')
        .eq('template_id', template.id)
        .order('order_index', { ascending: true });

      if (error) throw error;
      return data as TemplateItem[];
    },
  });

  const getPriorityLabel = (priority: string | null) => {
    switch (priority) {
      case 'essential': return { label: 'Essentiell', color: 'bg-red-100 text-red-700' };
      case 'recommended': return { label: 'Empfohlen', color: 'bg-amber-100 text-amber-700' };
      case 'optional': return { label: 'Optional', color: 'bg-slate-100 text-slate-600' };
      default: return { label: '-', color: 'bg-slate-50 text-slate-400' };
    }
  };

  const getTargetLabel = (mode: string | undefined) => {
    switch (mode) {
      case 'pregnancy': return 'Schwangerschaft';
      case 'baby': return 'Baby';
      case 'both': return 'Beide';
      default: return 'Beide';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-bold text-slate-900">{template.title}</h2>
            {template.description && (
              <p className="text-sm text-slate-500 mt-1">{template.description}</p>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg flex-shrink-0">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-6 border-b border-slate-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-xs text-slate-500 mb-1">Kategorie</div>
              <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full">
                {CATEGORY_LABELS[template.category] || template.category}
              </span>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Status</div>
              <span className={`text-sm font-medium ${template.is_active ? 'text-emerald-600' : 'text-slate-400'}`}>
                {template.is_active ? 'Aktiv' : 'Inaktiv'}
              </span>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Zielgruppe</div>
              <span className="text-sm text-slate-700">{getTargetLabel(template.target_mode)}</span>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Für Free-Nutzer</div>
              <span className={`text-sm font-medium ${template.visible_to_free ? 'text-blue-600' : 'text-amber-600'}`}>
                {template.visible_to_free ? 'Ja' : 'Nein'}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">
            Elemente ({items?.length || 0})
          </h3>
          {isLoading ? (
            <div className="text-center py-4 text-slate-500">Lädt...</div>
          ) : items && items.length > 0 ? (
            <div className="space-y-2">
              {items.map((item, index) => {
                const priority = getPriorityLabel(item.priority);
                return (
                  <div key={item.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <span className="text-xs text-slate-400 mt-0.5 w-6 text-right flex-shrink-0">{index + 1}.</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900">{item.title}</div>
                      {item.description && (
                        <div className="text-xs text-slate-500 mt-0.5">{item.description}</div>
                      )}
                      <div className="flex gap-2 mt-1.5 flex-wrap">
                        <span className={`px-1.5 py-0.5 text-xs rounded ${priority.color}`}>
                          {priority.label}
                        </span>
                        {item.quantity && (
                          <span className="text-xs text-slate-500">Menge: {item.quantity}</span>
                        )}
                        {item.estimated_cost_eur != null && item.estimated_cost_eur > 0 && (
                          <span className="text-xs text-slate-500">~{item.estimated_cost_eur}€</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-4 text-slate-500">Keine Elemente vorhanden</div>
          )}
        </div>

        <div className="flex justify-end p-6 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Main Page ---
export function ChecklistsPage() {
  const { contentLocale } = useAdminLocale();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editTemplate, setEditTemplate] = useState<ChecklistWithItems | null>(null);
  const [viewTemplate, setViewTemplate] = useState<ChecklistWithItems | null>(null);
  const pageSize = 10;
  const queryClient = useQueryClient();

  // Fetch checklists with pagination
  const { data: checklistsData, isLoading } = useQuery({
    queryKey: ['checklists', currentPage, searchQuery, categoryFilter, statusFilter, contentLocale],
    queryFn: async () => {
      let query = supabase
        .from('checklist_templates')
        .select('*', { count: 'exact' })
        .eq('locale', contentLocale);

      if (searchQuery) {
        query = query.ilike('title', `%${searchQuery}%`);
      }

      if (categoryFilter !== 'all') {
        query = query.eq('category', categoryFilter);
      }

      if (statusFilter === 'active') {
        query = query.eq('is_active', true);
      } else if (statusFilter === 'inactive') {
        query = query.eq('is_active', false);
      }

      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;

      const { data, count, error } = await query
        .order('order_index', { ascending: true })
        .range(from, to);

      if (error) throw error;

      const checklistIds = (data as ChecklistTemplate[]).map(c => c.id);

      let countMap: Record<string, number> = {};
      if (checklistIds.length > 0) {
        const { data: itemCounts, error: itemsError } = await supabase
          .from('checklist_template_items')
          .select('template_id')
          .in('template_id', checklistIds);

        if (itemsError) throw itemsError;

        (itemCounts as { template_id: string }[])?.forEach(item => {
          countMap[item.template_id] = (countMap[item.template_id] || 0) + 1;
        });
      }

      const checklistsWithCounts: ChecklistWithItems[] = (data as ChecklistTemplate[]).map(c => ({
        ...c,
        items_count: countMap[c.id] || 0,
      }));

      return {
        checklists: checklistsWithCounts,
        totalCount: count || 0,
      };
    },
  });

  // Fetch unique categories for filter
  const { data: categories } = useQuery({
    queryKey: ['checklist-categories', contentLocale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('checklist_templates')
        .select('category')
        .eq('locale', contentLocale);

      if (error) throw error;

      const uniqueCategories = [...new Set((data as { category: string }[]).map(d => d.category))];
      return uniqueCategories.sort();
    },
  });

  // Fetch KPI stats
  const { data: stats } = useQuery({
    queryKey: ['checklists-stats'],
    queryFn: async () => {
      const [totalResult, activeResult, itemsResult, usageResult, completedItemsResult, allUserItemsResult] = await Promise.all([
        supabase.from('checklist_templates').select('id', { count: 'exact', head: true }),
        supabase.from('checklist_templates').select('id', { count: 'exact', head: true }).eq('is_active', true),
        supabase.from('checklist_template_items').select('id', { count: 'exact', head: true }),
        supabase.from('user_checklists').select('id', { count: 'exact', head: true }),
        supabase.from('user_checklist_items').select('id', { count: 'exact', head: true }).eq('status', 'completed'),
        supabase.from('user_checklist_items').select('id', { count: 'exact', head: true }),
      ]);

      const completionRate = allUserItemsResult.count && completedItemsResult.count
        ? Math.round((completedItemsResult.count / allUserItemsResult.count) * 100)
        : 0;

      return {
        total: totalResult.count || 0,
        active: activeResult.count || 0,
        items: itemsResult.count || 0,
        usage: usageResult.count || 0,
        completionRate,
      };
    },
  });

  // Fetch template usage for pie chart
  const { data: templateUsage } = useQuery({
    queryKey: ['checklist-template-usage'],
    queryFn: async () => {
      const { data: templates, error: templatesError } = await supabase
        .from('checklist_templates')
        .select('id, title');

      if (templatesError) throw templatesError;

      const { data: userChecklists, error: ucError } = await supabase
        .from('user_checklists')
        .select('template_id');

      if (ucError) throw ucError;

      const templateMap: Record<string, string> = {};
      (templates as { id: string; title: string }[])?.forEach(t => {
        templateMap[t.id] = t.title;
      });

      const templateCounts: Record<string, { name: string; count: number }> = {};
      let customCount = 0;

      (userChecklists as { template_id: string | null }[])?.forEach(uc => {
        if (uc.template_id && templateMap[uc.template_id]) {
          if (!templateCounts[uc.template_id]) {
            templateCounts[uc.template_id] = { name: templateMap[uc.template_id], count: 0 };
          }
          templateCounts[uc.template_id].count++;
        } else {
          customCount++;
        }
      });

      const result = Object.values(templateCounts).map(t => ({
        name: t.name,
        value: t.count,
      }));

      if (customCount > 0) {
        result.push({ name: 'Eigene Listen', value: customCount });
      }

      return result.sort((a, b) => b.value - a.value);
    },
  });

  // Fetch completion rates per template for bar chart
  const { data: completionRates } = useQuery({
    queryKey: ['checklist-completion-rates'],
    queryFn: async () => {
      const { data: templates, error: templatesError } = await supabase
        .from('checklist_templates')
        .select('id, title');

      if (templatesError) throw templatesError;

      const rates: { name: string; rate: number }[] = [];

      for (const template of templates as { id: string; title: string }[]) {
        const { data: checklists } = await supabase
          .from('user_checklists')
          .select('id')
          .eq('template_id', template.id);

        if (checklists && checklists.length > 0) {
          const checklistIds = checklists.map((c: { id: string }) => c.id);

          const [totalResult, completedResult] = await Promise.all([
            supabase
              .from('user_checklist_items')
              .select('id', { count: 'exact', head: true })
              .in('checklist_id', checklistIds),
            supabase
              .from('user_checklist_items')
              .select('id', { count: 'exact', head: true })
              .in('checklist_id', checklistIds)
              .eq('status', 'completed'),
          ]);

          const total = totalResult.count || 0;
          const completed = completedResult.count || 0;
          const rate = total > 0 ? Math.round((completed / total) * 100) : 0;

          rates.push({
            name: template.title.length > 20 ? template.title.substring(0, 20) + '...' : template.title,
            rate,
          });
        }
      }

      return rates.sort((a, b) => b.rate - a.rate);
    },
  });

  // Fetch popular items
  const { data: popularItems } = useQuery({
    queryKey: ['checklist-popular-items'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_checklist_items')
        .select('title')
        .eq('status', 'completed');

      if (error) throw error;

      const itemCounts: Record<string, number> = {};
      (data as { title: string }[])?.forEach(item => {
        itemCounts[item.title] = (itemCounts[item.title] || 0) + 1;
      });

      return Object.entries(itemCounts)
        .map(([title, count]) => ({ title, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
    },
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('checklist_templates')
        .update({ is_active: isActive } as never)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      queryClient.invalidateQueries({ queryKey: ['checklists-stats'] });
      toast.success('Status aktualisiert');
    },
    onError: () => {
      toast.error('Fehler beim Aktualisieren');
    },
  });

  // Toggle visible_to_free mutation
  const toggleVisibleToFreeMutation = useMutation({
    mutationFn: async ({ id, visibleToFree }: { id: string; visibleToFree: boolean }) => {
      const { error } = await supabase
        .from('checklist_templates')
        .update({ visible_to_free: visibleToFree } as never)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      toast.success('Sichtbarkeit aktualisiert');
    },
    onError: () => {
      toast.error('Fehler beim Aktualisieren');
    },
  });

  // Toggle target_mode mutation
  const toggleTargetModeMutation = useMutation({
    mutationFn: async ({ id, currentMode }: { id: string; currentMode: 'pregnancy' | 'baby' | 'both' }) => {
      const nextMode = currentMode === 'pregnancy' ? 'baby' : currentMode === 'baby' ? 'both' : 'pregnancy';
      const { error } = await supabase
        .from('checklist_templates')
        .update({ target_mode: nextMode } as never)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      toast.success('Zielgruppe aktualisiert');
    },
    onError: () => {
      toast.error('Fehler beim Aktualisieren');
    },
  });

  // Create template mutation (with items)
  const createMutation = useMutation({
    mutationFn: async (data: TemplateFormData) => {
      const { data: created, error } = await supabase
        .from('checklist_templates')
        .insert({
          title: data.title,
          description: data.description || null,
          category: data.category,
          order_index: data.order_index,
          is_active: data.is_active,
          visible_to_free: data.visible_to_free,
          target_mode: data.target_mode,
          locale: contentLocale,
        } as never)
        .select('id')
        .single();

      if (error) throw error;

      const templateId = (created as { id: string }).id;

      // Insert items
      if (data.items.length > 0) {
        const itemInserts = data.items.map((item, index) => ({
          template_id: templateId,
          title: item.title,
          description: item.description || null,
          quantity: item.quantity || null,
          priority: item.priority,
          estimated_cost_eur: item.estimated_cost_eur,
          order_index: index,
          locale: contentLocale,
        }));

        const { error: itemsError } = await supabase
          .from('checklist_template_items')
          .insert(itemInserts as never[]);

        if (itemsError) throw itemsError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      queryClient.invalidateQueries({ queryKey: ['checklists-stats'] });
      queryClient.invalidateQueries({ queryKey: ['checklist-categories'] });
      setShowCreateModal(false);
      toast.success('Vorlage erstellt');
    },
    onError: () => {
      toast.error('Fehler beim Erstellen');
    },
  });

  // Update template mutation (with items sync)
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: TemplateFormData }) => {
      // Update template
      const { error } = await supabase
        .from('checklist_templates')
        .update({
          title: data.title,
          description: data.description || null,
          category: data.category,
          order_index: data.order_index,
          is_active: data.is_active,
          visible_to_free: data.visible_to_free,
          target_mode: data.target_mode,
          locale: contentLocale,
        } as never)
        .eq('id', id);

      if (error) throw error;

      // Get existing items to determine what to create/update/delete
      const { data: existingItems, error: fetchError } = await supabase
        .from('checklist_template_items')
        .select('id')
        .eq('template_id', id);

      if (fetchError) throw fetchError;

      const existingIds = new Set((existingItems as { id: string }[]).map(i => i.id));
      const currentIds = new Set(data.items.filter(i => i.id).map(i => i.id!));

      // Delete removed items
      const toDelete = [...existingIds].filter(eId => !currentIds.has(eId));
      if (toDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('checklist_template_items')
          .delete()
          .in('id', toDelete);
        if (deleteError) throw deleteError;
      }

      // Update existing items and insert new ones
      for (let i = 0; i < data.items.length; i++) {
        const item = data.items[i];
        const payload = {
          template_id: id,
          title: item.title,
          description: item.description || null,
          quantity: item.quantity || null,
          priority: item.priority,
          estimated_cost_eur: item.estimated_cost_eur,
          order_index: i,
          locale: contentLocale,
        };

        if (item.id) {
          // Update existing
          const { error: updateError } = await supabase
            .from('checklist_template_items')
            .update(payload as never)
            .eq('id', item.id);
          if (updateError) throw updateError;
        } else {
          // Insert new
          const { error: insertError } = await supabase
            .from('checklist_template_items')
            .insert(payload as never);
          if (insertError) throw insertError;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      queryClient.invalidateQueries({ queryKey: ['checklists-stats'] });
      queryClient.invalidateQueries({ queryKey: ['checklist-categories'] });
      queryClient.invalidateQueries({ queryKey: ['checklist-template-items'] });
      setEditTemplate(null);
      toast.success('Vorlage aktualisiert');
    },
    onError: () => {
      toast.error('Fehler beim Aktualisieren');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await supabase
        .from('checklist_template_items')
        .delete()
        .eq('template_id', id);

      const { error } = await supabase
        .from('checklist_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      queryClient.invalidateQueries({ queryKey: ['checklists-stats'] });
      toast.success('Checkliste gelöscht');
    },
    onError: () => {
      toast.error('Fehler beim Löschen');
    },
  });

  const totalPages = Math.ceil((checklistsData?.totalCount || 0) / pageSize);

  const getTargetModeInfo = (mode: 'pregnancy' | 'baby' | 'both' | undefined) => {
    switch (mode) {
      case 'pregnancy':
        return { label: 'SSW', icon: HeartPulse, color: 'text-pink-600' };
      case 'baby':
        return { label: 'Baby', icon: Baby, color: 'text-sky-600' };
      case 'both':
      default:
        return { label: 'Beide', icon: Users, color: 'text-purple-600' };
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  return (
    <div>
      {/* Modals */}
      {showCreateModal && (
        <TemplateFormModal
          onClose={() => setShowCreateModal(false)}
          onSave={(data) => createMutation.mutate(data)}
          isSaving={createMutation.isPending}
        />
      )}
      {editTemplate && (
        <TemplateFormModal
          template={editTemplate}
          onClose={() => setEditTemplate(null)}
          onSave={(data) => updateMutation.mutate({ id: editTemplate.id, data })}
          isSaving={updateMutation.isPending}
        />
      )}
      {viewTemplate && (
        <TemplateViewModal
          template={viewTemplate}
          onClose={() => setViewTemplate(null)}
        />
      )}

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-bold text-slate-900">Checklisten-Vorlagen</h1>
          <LocaleSelector />
        </div>
        <p className="text-slate-600">Vorgefertigte Checklisten verwalten</p>
      </div>

      {/* KPI Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
              <ClipboardList className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.total || 0}</div>
              <div className="text-sm text-slate-600">Vorlagen</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.active || 0}</div>
              <div className="text-sm text-slate-600">Aktiv</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <ListChecks className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.items || 0}</div>
              <div className="text-sm text-slate-600">Elemente</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.usage || 0}</div>
              <div className="text-sm text-slate-600">Nutzungen</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.completionRate || 0}%</div>
              <div className="text-sm text-slate-600">Abschlussrate</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <form onSubmit={handleSearch} className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Nach Titel suchen..."
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
              />
            </div>
          </form>
          <div className="flex gap-2">
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
            >
              <option value="all">Alle Kategorien</option>
              {categories?.map((cat) => (
                <option key={cat} value={cat}>{CATEGORY_LABELS[cat] || cat}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as StatusFilter);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
            >
              <option value="all">Alle Status</option>
              <option value="active">Aktiv</option>
              <option value="inactive">Inaktiv</option>
            </select>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-medium flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Neue Vorlage
            </button>
          </div>
        </div>
      </div>

      {/* Checklists Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase w-8"></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Titel</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Elemente</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Reihenfolge</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Zielgruppe</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Für Free</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                    Lädt...
                  </td>
                </tr>
              ) : checklistsData?.checklists?.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                    Keine Checklisten gefunden
                  </td>
                </tr>
              ) : (
                checklistsData?.checklists?.map((checklist) => (
                  <tr key={checklist.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <GripVertical className="w-4 h-4 text-slate-400 cursor-grab" />
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <span className="text-sm font-medium text-slate-900">{checklist.title}</span>
                        {checklist.description && (
                          <div className="text-xs text-slate-500 truncate max-w-xs">{checklist.description}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-900">{checklist.items_count}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-600">{checklist.order_index}</span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleActiveMutation.mutate({ id: checklist.id, isActive: !checklist.is_active })}
                        className="focus:outline-none"
                      >
                        {checklist.is_active ? (
                          <span className="flex items-center gap-1 text-emerald-600 text-sm">
                            <CheckCircle className="w-4 h-4" />
                            Aktiv
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-slate-400 text-sm">
                            <XCircle className="w-4 h-4" />
                            Inaktiv
                          </span>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const modeInfo = getTargetModeInfo(checklist.target_mode);
                        const Icon = modeInfo.icon;
                        return (
                          <button
                            onClick={() => toggleTargetModeMutation.mutate({ id: checklist.id, currentMode: checklist.target_mode || 'both' })}
                            className="focus:outline-none"
                            title="Klicken zum Wechseln: SSW → Baby → Beide"
                          >
                            <span className={`flex items-center gap-1 ${modeInfo.color} text-sm`}>
                              <Icon className="w-4 h-4" />
                              {modeInfo.label}
                            </span>
                          </button>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleVisibleToFreeMutation.mutate({ id: checklist.id, visibleToFree: !checklist.visible_to_free })}
                        className="focus:outline-none"
                        title={checklist.visible_to_free ? 'Sichtbar für Free-Nutzer' : 'Nur für Premium/Beta'}
                      >
                        {checklist.visible_to_free ? (
                          <span className="flex items-center gap-1 text-blue-600 text-sm">
                            <User className="w-4 h-4" />
                            Ja
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-amber-600 text-sm">
                            <Crown className="w-4 h-4" />
                            Nein
                          </span>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setViewTemplate(checklist)}
                          className="p-1 hover:bg-slate-100 rounded"
                          title="Ansehen"
                        >
                          <Eye className="w-4 h-4 text-slate-500" />
                        </button>
                        <button
                          onClick={() => setEditTemplate(checklist)}
                          className="p-1 hover:bg-slate-100 rounded"
                          title="Bearbeiten"
                        >
                          <Edit className="w-4 h-4 text-slate-500" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm('Checkliste wirklich löschen? Alle zugehörigen Elemente werden ebenfalls gelöscht.')) {
                              deleteMutation.mutate(checklist.id);
                            }
                          }}
                          className="p-1 hover:bg-slate-100 rounded"
                          title="Löschen"
                        >
                          <Trash2 className="w-4 h-4 text-slate-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
          <div className="text-sm text-slate-600">
            Zeige {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, checklistsData?.totalCount || 0)} von {checklistsData?.totalCount?.toLocaleString('de-DE') || 0}
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: Math.min(5, totalPages || 1) }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1 border border-slate-300 rounded ${
                  currentPage === page
                    ? 'bg-teal-500 text-white border-teal-500'
                    : 'hover:bg-slate-100'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages || 1, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Section */}
      <div className="mt-8 space-y-6">
        <h2 className="text-xl font-bold text-slate-900">Nutzungsstatistiken</h2>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Template Usage Pie Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <CheckSquare className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Vorlagen-Nutzung</h3>
                <p className="text-sm text-slate-500">Wie oft wird jede Vorlage verwendet?</p>
              </div>
            </div>
            <div className="h-[300px]">
              {templateUsage && templateUsage.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={templateUsage}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                      labelLine={false}
                    >
                      {templateUsage.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-500">
                  Keine Daten verfügbar
                </div>
              )}
            </div>
            {templateUsage && templateUsage.length > 0 && (
              <div className="flex flex-wrap gap-3 mt-4 justify-center">
                {templateUsage.map((item, index) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                    />
                    <span className="text-xs text-slate-600">{item.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Completion Rates Bar Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Target className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Abschlussraten pro Vorlage</h3>
                <p className="text-sm text-slate-500">Wie viele Items werden abgeschlossen?</p>
              </div>
            </div>
            <div className="h-[300px]">
              {completionRates && completionRates.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={completionRates} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} unit="%" />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={100}
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                    />
                    <Tooltip
                      formatter={(value: number) => [
                        `${value}%`,
                        'Abschlussrate',
                      ]}
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                      }}
                    />
                    <Bar dataKey="rate" fill="#14b8a6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-500">
                  Keine Daten verfügbar
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Popular Items */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <Award className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Beliebteste Items</h3>
              <p className="text-sm text-slate-500">Am häufigsten abgeschlossene Aufgaben</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {popularItems?.map((item, index) => (
              <div
                key={item.title}
                className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg"
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-white ${
                  index < 3 ? 'bg-amber-500' : 'bg-slate-400'
                }`}>
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{item.title}</div>
                  <div className="text-xs text-slate-500">{item.count}x abgeschlossen</div>
                </div>
              </div>
            ))}
            {(!popularItems || popularItems.length === 0) && (
              <div className="col-span-2 text-center py-8 text-slate-500">
                Noch keine abgeschlossenen Items
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
